import type {
  ClubKits,
  KitRank,
  MatchKitChoice,
  MatchKitPut,
  MatchKitRead,
  TeamKits,
} from '@volee/api-client'
import { isHexColour, type ClubKit, type KitAppearance } from '@volee/ui'

import { kitLabel } from './kit-label.ts'
import {
  coloursOf,
  EMPTY_KIT_FIELDS,
  fromKit,
  toKitPut,
  type KitFieldErrors,
  type KitFields,
} from './kit-fields.ts'
import {
  CLUB_KIT_NAMES,
  KIT_RANK_ORDER,
  MATCH_KIT_CHOICES,
  type PlayerPosition,
} from '../messages/index.ts'

// La divisa di gara of a call-up: which kit a side wears, and the kit itself (ADR 0001).

/** The choice and the kit it stands for, as the call-up holds it until the convocazione is saved. */
export interface MatchKitDraft {
  readonly choice: MatchKitChoice
  readonly kit: KitFields
}

export function fromMatchKit(saved: MatchKitRead | null): MatchKitDraft | null {
  return saved === null ? null : { choice: saved.choice, kit: fromKit(saved) }
}

/** The società's and the squad's kits go by name, since the server copies them; other colours go whole. */
export function toMatchKitPut(draft: MatchKitDraft | null): MatchKitPut | null {
  if (draft === null) return null
  if (draft.choice === 'other') return { choice: 'other', ...toKitPut(draft.kit) }
  return { choice: draft.choice }
}

/** Whose kits a choice names, and which choice each rank of each owner is: the one place the
 *  pairs are written, so a rank the contract adds is missing from here and nowhere else. */
type KitOwnerName = 'team' | 'club'
const CHOICE_OF: Record<KitOwnerName, Record<KitRank, MatchKitChoice>> = {
  team: { first: 'team_first', second: 'team_second' },
  club: { first: 'first', second: 'second' },
}

/** The rank a choice stands for, whoever owns the kit: `null` for other colours, which are no rank's. Read
 *  off the pairs above, so a message that names a kit by its rank has no table of its own to forget. */
export function rankOfChoice(choice: MatchKitChoice): KitRank | null {
  const owners = Object.values(CHOICE_OF)
  return KIT_RANK_ORDER.find((rank) => owners.some((owner) => owner[rank] === choice)) ?? null
}

/** The kits a side may wear, in the order the step deals them: the squad's two, then the
 *  società's two. Each as the convocazione would store it. The one already saved shows its copy,
 *  which the server keeps when chosen again, and stays offered when its owner has since removed it. */
export function kitOptions(
  clubKits: ClubKits,
  teamKits: TeamKits,
  saved: MatchKitDraft | null,
): readonly MatchKitDraft[] {
  const ofKits = (owner: KitOwnerName, kits: ClubKits | TeamKits) =>
    KIT_RANK_ORDER.flatMap((rank) => {
      const choice = CHOICE_OF[owner][rank]
      if (saved?.choice === choice) return [saved]
      const kit = kits[rank]
      return kit === null ? [] : [{ choice, kit: fromKit(kit) }]
    })
  return [...ofKits('team', teamKits), ...ofKits('club', clubKits)]
}

/** Whether the squad has a kit to offer, its saved copy included: then the società's say whose
 *  they are. The one rule of the step's cards and of the summary after it. */
export function squadHasKits(teamKits: TeamKits, saved: MatchKitDraft | null): boolean {
  return KIT_RANK_ORDER.some(
    (rank) => teamKits[rank] !== null || saved?.choice === CHOICE_OF.team[rank],
  )
}

/** The name of a choice: the società's kits keep their plain name until a squad's stand beside
 *  them, then they say whose they are. */
export function matchKitName(choice: MatchKitChoice, sayWhose: boolean): string {
  const clubRank = KIT_RANK_ORDER.find((rank) => CHOICE_OF.club[rank] === choice)
  return sayWhose && clubRank !== undefined ? CLUB_KIT_NAMES[clubRank] : MATCH_KIT_CHOICES[choice]
}

/** The kit put forward while the convocazione wears none: the first of the squad, else the società's,
 *  among those offered. */
export function proposedChoice(options: readonly MatchKitDraft[]): MatchKitChoice | null {
  const proposals = [CHOICE_OF.team.first, CHOICE_OF.club.first]
  return proposals.find((choice) => options.some((option) => option.choice === choice)) ?? null
}

/** The step opens on the kit the convocazione wears, else on the one proposed: proposed, not chosen.
 *  Never on a kit it does not offer, which no card could show checked. */
export function openingChoice(
  current: MatchKitDraft | null,
  options: readonly MatchKitDraft[],
  proposed: MatchKitChoice | null,
): MatchKitChoice | null {
  if (current === null) return proposed
  const offered =
    current.choice === 'other' || options.some((option) => option.choice === current.choice)
  return offered ? current.choice : null
}

/** «Altri colori» opens on the colours already given for this match, else on the kit proposed —
 *  for bibs only the shirt changes — else empty: never a colour nobody chose. */
export function otherColoursAtStart(
  current: MatchKitDraft | null,
  options: readonly MatchKitDraft[],
  proposed: MatchKitChoice | null,
): KitFields {
  if (current?.choice === 'other') return current.kit
  return options.find((option) => option.choice === proposed)?.kit ?? EMPTY_KIT_FIELDS
}

/** Every colour the design shows, each a hex colour: the drawing's own rule for «not chosen yet».
 *  Asked here, unlike on the kits page, because the convocazione is saved two steps later, away from
 *  the field a refusal would belong to. */
export function missingColours(
  fields: KitFields,
  requiredText: string,
  notAColourText: string,
): KitFieldErrors {
  const errors: Record<string, string> = {}
  for (const name of coloursOf(fields.design)) {
    const colour = fields[name].trim()
    if (colour === '') errors[name] = requiredText
    else if (!isHexColour(colour)) errors[name] = notAColourText
  }
  return errors
}

/** The kit of the match as the drawing takes it: the colours the convocazione copied, nothing else. */
export function matchKitAppearance(saved: MatchKitRead | null): KitAppearance | null {
  if (saved === null) return null
  return {
    design: saved.design,
    primaryColor: saved.primary_color,
    secondaryColor: saved.secondary_color,
    shortsColor: saved.shorts_color,
  }
}

/** The shirt a player is drawn in: the kit of the match, except for a goalkeeper, whose shirt is not
 *  part of it. `null` is the neutral shirt, also for a convocazione with no kit set. */
export function shirtKit(
  position: PlayerPosition | null,
  kit: KitAppearance | null,
): KitAppearance | null {
  return position === 'goalkeeper' ? null : kit
}

/** The kit with its name, for the family's answer: «Prima divisa». The società's kits keep their
 *  plain name here, since a family never sees the squad's beside them to tell them apart. */
export function matchKitFigure(saved: MatchKitRead | null): ClubKit | null {
  const draft = fromMatchKit(saved)
  const appearance = matchKitAppearance(saved)
  if (draft === null || appearance === null) return null
  return { ...appearance, caption: matchKitName(draft.choice, false), label: kitLabel(draft.kit) }
}
